import DocumentNotFoundModal from "@/components/DocumentNotFoundModal";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { APP_VERSION } from "@/config/version";
import { useToast } from "@/contexts/ToastContext";
import { validateDocument } from "@/services";
import { setDocumentStatus } from "@/services/Document/document-service";
import { AuthStep } from "@/types";
import { removeMaskCPF } from "@/utils/Validations/validations";
import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";

// Função para obter a chave do reCAPTCHA
const getRecaptchaSiteKey = () => {
  return import.meta.env.VITE_RECAPTCHA_SITE_KEY;
};

const Login = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [document, setDocument] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDocumentNotFoundModal, setShowDocumentNotFoundModal] = useState(false);
  const [documentType, setDocumentType] = useState<'CPF' | 'CNPJ'>('CPF');
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    
    try {
      // Remover máscara do documento
      const documentWithoutMask = removeMaskCPF(document);
      
      // Primeiro, tentar validar o documento via API
      try {
        const validationResponse = await validateDocument({ document: documentWithoutMask });
        
        // Salvar o status do documento no localStorage (similar ao sessionId)
        setDocumentStatus(validationResponse.status);
        
        // Redirecionar baseado no nextStep retornado pela API
        if (validationResponse.nextStep === AuthStep.SELECT_PHONE) {
          navigate('/phone-selection');
        } else if (validationResponse.nextStep === AuthStep.CONFIRM_AUTHENTICATION) {
          navigate('/success');
        } else {
          // Fallback para casos não esperados
          console.warn(`NextStep não reconhecido: ${validationResponse.nextStep}`);
          navigate('/phone-selection');
        }
      } catch (apiError: any) {
        // Se retornar 404, mostrar modal de documento não elegível
        if (apiError.response?.status === 404) {
          console.log("API retornou 404, documento não elegível");
          setShowDocumentNotFoundModal(true);
        } else {
          // Outros erros da API
          throw apiError;
        }
      }
    } catch (error) {
      console.error("Erro ao validar documento:", error);
      showToast({
        title: "Erro ao validar documento. Tente novamente.",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDocument = (value: string) => {
    const numbers = value.replace(/\D/g, "");

    if (numbers.length <= 11) {
      // Formato CPF: 000.000.000-00
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else {
      // Formato CNPJ: 00.000.000/0000-00
      return numbers
        .slice(0, 14)
        .replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDocument(e.target.value);
    setDocument(formatted);
    
    // Determinar tipo de documento baseado no tamanho
    const numbers = formatted.replace(/\D/g, "");
    if (numbers.length <= 11) {
      setDocumentType('CPF');
    } else {
      setDocumentType('CNPJ');
    }
    
    // Se o documento foi apagado e o reCAPTCHA vai sumir, resetar o captcha
    if (formatted.length < 11 && captcha) {
      setCaptcha(null);
      recaptchaRef.current?.reset();
    }
  };

  // Handlers do reCAPTCHA
  const handleCaptchaChange = (value: string | null) => {
    setCaptcha(value);
  };

  const handleCaptchaExpired = () => {
    setCaptcha(null);
    recaptchaRef.current?.reset();
    showToast({
      title: "Captcha expirado. Por favor, confirme o captcha novamente.",
      type: "error"
    });
  };

  const handleCaptchaErrored = () => {
    setCaptcha(null);
    recaptchaRef.current?.reset();
    showToast({
      title: "Erro no reCAPTCHA. Não foi possível validar o captcha. Tente novamente.",
      type: "error"
    });
  };

  // Função para limpar a tela de login
  const clearLoginForm = () => {
    setDocument("");
    setCaptcha(null);
    setDocumentType('CPF');
    recaptchaRef.current?.reset();
  };

  return (
    <div className="min-h-screen bg-background-gradient-primary">
      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-5">
        {/* Login Card */}
        <div className="w-full max-w-lg">
          <Card className="shadow-xl border-0 backdrop-blur-sm md:px-6 py-4 md:py-6 gap-0">
            <CardHeader className="pb-0">
              {/* Header with Back Button and Logo */}
              <div className="mb-6 flex items-center justify-between">
                <div className="w-10"></div> {/* Spacer para centralizar o logo */}
                <img
                  src="/icon_oi_tv.svg"
                  alt="Oi TV"
                  className="h-12 w-auto object-contain"
                />
                <div className="w-10"></div> {/* Spacer para manter o logo centralizado */}
              </div>

              {/* Welcome Section */}
              <div className="space-y-2">
                <h1 className="text-2xl font-bold">
                  Ative a sua 2º tela
                </h1>
                <p className="text-sm text-gray-600">
                  Digite seu CPF ou CNPJ do titular da sua conta Oi TV para ativar o seu acesso
                </p>
              </div>
            </CardHeader>

            <CardContent className="pt-4">
              <form onSubmit={handleSubmit}>
                <div className="md:pb-24 pb-12 flex flex-col gap-4">
                  <Label htmlFor="document">Digite seu CPF ou CNPJ para entrar</Label>
                  <Input
                    id="document"
                    type="text"
                    placeholder="CPF ou CNPJ"
                    value={document}
                    onChange={handleDocumentChange}
                    maxLength={18}
                    required
                    className="tracking-wider"
                    disabled={isLoading}
                  />
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    className={`${
                      document.length >= 11 ? "block" : "hidden"
                    }`}
                    sitekey={getRecaptchaSiteKey()}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    onErrored={handleCaptchaErrored}
                    hl="pt-BR"
                  />
                </div>

                <div className="space-y-2">
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-green-600"
                    disabled={isLoading || (document.length < 14 && document.length !== 11) || !captcha}
                  >
                    {isLoading ? "Continuando..." : <>Continuar</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="flex justify-center items-center mt-4">
            <span className="text-xs text-white">{APP_VERSION}</span>
          </div>
        </div>
      </div>

      {/* Modal Documento não encontrado */}
      <DocumentNotFoundModal
        isOpen={showDocumentNotFoundModal}
        onClose={() => {
          setShowDocumentNotFoundModal(false);
          clearLoginForm();
        }}
        documentType={documentType}
      />
    </div>
  );
};

export default Login;